import { Injectable } from '@angular/core';
import { Subject } from 'rxjs';
import { first } from 'rxjs/operators';
import { isInSubnet } from 'subnet-check';
import { ApiService } from '../../providers/api/api.service';
import { CommonRegEx } from '../../validators/match.pattern.validator';
import { EndpointService } from '../endpoint/endpoint.service';
import { ArrayService } from '../array/array.service';

@Injectable({
  providedIn: 'root'
})
export class IPsService {
  SUBNET_MAP = {
    '1': '128.0.0.0',
    '2': '192.0.0.0',
    '3': '224.0.0.0',
    '4': '240.0.0.0',
    '5': '248.0.0.0',
    '6': '252.0.0.0',
    '7': '254.0.0.0',
    '8': '255.0.0.0',
    '9': '255.128.0.0',
    '10': '255.192.0.0',
    '11': '255.224.0.0',
    '12': '255.240.0.0',
    '13': '255.248.0.0',
    '14': '255.252.0.0',
    '15': '255.254.0.0',
    '16': '255.255.0.0',
    '17': '255.255.128.0',
    '18': '255.255.192.0',
    '19': '255.255.224.0',
    '20': '255.255.240.0',
    '21': '255.255.248.0',
    '22': '255.255.252.0',
    '23': '255.255.254.0',
    '24': '255.255.255.0',
    '25': '255.255.255.128',
    '26': '255.255.255.192',
    '27': '255.255.255.224',
    '28': '255.255.255.240',
    '29': '255.255.255.248',
    '30': '255.255.255.252',
    '31': '255.255.255.254',
    '32': '255.255.255.255'
  };
  constructor(
    private readonly apiService: ApiService,
    private readonly endpointService: EndpointService,
    private arrayService: ArrayService
  ) {}

  // eslint-disable-next-line
  // public getIPAddressesByUserID(userID: number): Subject<string[]> {
  //   const userObs: Subject<string[]> = new Subject();

  //   this.dataCacheService.users.subscribe(users => {
  //     userObs.next(
  //       users
  //         .filter(user => user.id === userID)
  //         .map(user => (user?.ip && !user?.isDeleted ? user.ip : null))
  //     );
  //   });

  //   return userObs;
  // }

  // eslint-disable-next-line
  public getIPAddressesByUserIDAsync(
    userId: number,
    startTime: number,
    endTime: number
  ): Subject<string[]> {
    const ipAddressesObs: Subject<string[]> = new Subject();
    this.endpointService.getIPAddressesByUserID(userId, startTime, endTime).subscribe(ips => {
      ipAddressesObs.next(ips.ips?.length ? ips.ips : null);
    });
    return ipAddressesObs;
  }

  // eslint-disable-next-line
  // public getSiteIDsByIPAddress(ip: string): Subject<number[]> {
  //   const siteIdObs: Subject<number[]> = new Subject();

  //   this.dataCacheService.sites.subscribe(sites => {
  //     const siteIds = [];
  //     sites.forEach(site => {
  //       if (site?.sitesData?.subnets?.length) {
  //         site?.sitesData?.subnets.forEach(subnet => {
  //           if (this.isIPInSubnet(ip, subnet.subnet)) {
  //             siteIds.push(site.sitesData.id);
  //           }
  //         });
  //       }
  //     });
  //     siteIdObs.next(siteIds);
  //   });

  //   return siteIdObs;
  // }

  // eslint-disable-next-line
  // public getSiteIDsByIPAddresses(ips: string[]): Subject<number[]> {
  //   const result = [];
  //   const resultSubject: Subject<number[]> = new Subject();

  //   ips.forEach(ip => {
  //     this.getSiteIDsByIPAddress(ip)
  //       .pipe(first())
  //       .subscribe(endpointIps => {
  //         result.push(endpointIps);
  //         // if all subscriptions returned data
  //         if (result.length === ips.length) {
  //           resultSubject.next(
  //             this.arrayService.uniqueArray(this.arrayService.mergeMultiDimArray(result))
  //           );
  //         }
  //       });
  //   });

  //   return resultSubject;
  // }

  // eslint-disable-next-line
  public getSiteIDsByIPAddressAsync(ip: string): Subject<number[]> {
    const siteIdsObs: Subject<number[]> = new Subject();
    this.endpointService.getSiteIDsByIPAddress(ip).subscribe(site => {
      siteIdsObs.next(site[0]?.siteIds?.length ? site[0].siteIds : null);
    });
    return siteIdsObs;
  }

  /**
   * Returns the version of an IP
   * @param ip {string} The IP used for evaluation
   */
  public getIPVersion(ip: string): 4 | 6 {
    if (this.isIPv4(ip)) {
      return 4;
    }

    if (this.isIPv6(ip)) {
      return 6;
    }

    return null;
  }

  /**
   * Checks if a string is a valid ip - either v4 or v6
   * @param ip {string} The IP used for evaluation
   */
  public isValidIP(ip: string): boolean {
    return CommonRegEx.IP_V4_V6.test(ip);
  }

  /**
   * Checks if a string is an ipv4
   * @param ip {string} The IP used for evaluation
   */
  public isIPv4(ip: string): boolean {
    return CommonRegEx.IP_REGEX.test(ip);
  }

  /**
   * Checks if a string is an ipv6
   * @param ip {string} The IP used for evaluation
   */
  public isIPv6(ip: string): boolean {
    return CommonRegEx.IPV6.test(ip);
  }

  /**
   * Checks if a string is a ipv4 subnet
   * @param ip {string} The IP used for evaluation
   */
  public isIPv4Subnet(ip: string): boolean {
    return CommonRegEx.IP_V4_CIDR.test(ip);
  }

  /**
   * Checks if a string is a ipv6 subnet
   * @param ip {string} The IP used for evaluation
   */
  public isIPv6Subnet(ip: string): boolean {
    return CommonRegEx.IP_V6_CIDR.test(ip);
  }

  /**
   * Checks if an ipv4 or ipv6 belongs to a subnet
   * @param ip {string} The IP used for evaluation
   * @param subnet {string} The subnet used for evaluation
   */
  public isIPInSubnet(ip: string, subnet: string): boolean {
    return isInSubnet(ip, subnet);
  }

  getSubnetByMapValue(subnet: string): string {
    subnet = subnet.toString();
    if (subnet && !subnet.includes('.')) {
      return this.SUBNET_MAP[subnet] ?? subnet;
    }
    return subnet;
  }
}
